import React from 'react';
import { ButtonBase, Grid, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { useTranslation } from 'react-i18next';
import { WizardStep } from './wizardSteps';

const STEP_LABEL_KEYS: Record<WizardStep, string> = {
  [WizardStep.WhatWhen]: 'calendar.create_order.steps.whatWhen',
  [WizardStep.Customer]: 'calendar.create_order.steps.customer',
  [WizardStep.Extras]: 'calendar.create_order.steps.extras',
};

const STEPS = [WizardStep.WhatWhen, WizardStep.Customer, WizardStep.Extras];

enum StepState {
  Current = 'current',
  Done = 'done',
  Error = 'error',
  ToDo = 'todo',
}

const CIRCLE_SX_BY_STATE = {
  [StepState.Current]: { bgcolor: 'primary.main', color: '#fff', borderColor: 'primary.main' },
  [StepState.Done]: { bgcolor: 'mb.mint', color: 'primary.main', borderColor: 'mb.mint' },
  [StepState.Error]: { bgcolor: 'error.light', color: 'error.main', borderColor: 'error.light' },
  [StepState.ToDo]: { bgcolor: 'background.paper', color: 'text.secondary', borderColor: 'mb.control' },
};

type WizardStepperProps = {
  step: WizardStep;
  stepErrors: Set<WizardStep>;
  onSelect: (step: WizardStep) => void;
};

export const WizardStepper = ({ step, stepErrors, onSelect }: WizardStepperProps) => {
  const { t } = useTranslation();

  const stateOf = (item: WizardStep): StepState => {
    if (stepErrors.has(item)) return StepState.Error;
    if (item === step) return StepState.Current;
    return item < step ? StepState.Done : StepState.ToDo;
  };

  return (
    <Grid
      container
      wrap="nowrap"
      sx={{
        flexShrink: 0,
        width: 'auto',
        mx: { xs: '-12px', md: '-24px' },
        mb: 1.5,
        borderBottom: '1px solid',
        borderColor: 'mb.lineSoft',
      }}>
      {STEPS.map((item) => {
        const state = stateOf(item);
        const isCurrent = item === step;
        const hasError = state === StepState.Error;
        return (
          <ButtonBase
            key={item}
            aria-current={isCurrent ? 'step' : undefined}
            onClick={() => onSelect(item)}
            sx={{
              flex: 1,
              minWidth: 0,
              gap: 1,
              px: 1,
              py: 1.25,
              justifyContent: 'center',
              bgcolor: isCurrent ? 'mb.ground' : undefined,
              boxShadow: (theme) =>
                isCurrent
                  ? `inset 0 -3px 0 ${hasError ? theme.palette.error.main : theme.palette.primary.main}`
                  : undefined,
            }}>
            <Grid
              container
              alignItems="center"
              justifyContent="center"
              sx={{
                width: 26,
                height: 26,
                flexShrink: 0,
                borderRadius: '50%',
                border: '1px solid',
                fontSize: 13,
                fontWeight: 700,
                ...CIRCLE_SX_BY_STATE[state],
              }}>
              {state === StepState.Done ? <CheckRoundedIcon sx={{ fontSize: 16 }} /> : hasError ? '!' : item}
            </Grid>
            <Typography
              noWrap
              sx={{
                fontSize: 13,
                fontWeight: isCurrent ? 700 : 500,
                color: hasError ? 'error.main' : isCurrent ? 'text.primary' : 'text.secondary',
              }}>
              {t(STEP_LABEL_KEYS[item])}
            </Typography>
          </ButtonBase>
        );
      })}
    </Grid>
  );
};
